import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  BlockedContactIcon,
  QuarantineContactIcon,
  SuspendedContactIcon,
  UnsafeContactIcon,
} from '@/lib/icon';
import { IconSvg } from '@/lib/v2/components';
import { InsightCardProps } from '@/lib/v2/examples/InsightCard/InsightCard';

import { IStatusList } from '@/src/ContactsModule/interfaces/Interfaces';

import { FilterCardsList } from '@/modules/ContactsModule/components';
import { useMutationContactsContext } from '@/modules/ContactsModule/contexts/ContactsContext';

interface ContactCardsDiscardedProps {
  statusList: IStatusList[];
  titleIsLoading?: boolean;
}

const CardsFilterDiscarded = ({
  statusList,
  titleIsLoading = true,
}: ContactCardsDiscardedProps) => {
  const { t } = useTranslation();
  const { setStatusFilter } = useMutationContactsContext();

  const cardList = useMemo<(InsightCardProps & { filter: string })[]>(
    () => [
      {
        icon: (
          <IconSvg
            fullWidth
            rounded
            bgColor="tertiary"
            svgComponent={<UnsafeContactIcon />}
            withPadding="medium"
          />
        ),
        description: t('STATUS_DISCARDED_CARDS.unsafe'),
        title: 0,
        tooltip: t('STATUS_DISCARDED_CARDS.unsafeTooltip'),
        isLoading: true,
        filter: 'unsafe',
        onClick: () => {
          setStatusFilter(['unsafe']);
        },
        id: 'unsafe-card',
      },
      {
        icon: (
          <IconSvg
            fullWidth
            rounded
            bgColor="tertiary"
            svgComponent={<SuspendedContactIcon />}
            withPadding="medium"
          />
        ),
        description: t('STATUS_DISCARDED_CARDS.suspended'),
        title: 0,
        tooltip: t('STATUS_DISCARDED_CARDS.suspendedTooltip'),
        isLoading: true,
        filter: 'suspended',
        onClick: () => {
          setStatusFilter(['suspended']);
        },
        id: 'suspended-card',
      },
      {
        icon: (
          <IconSvg
            fullWidth
            rounded
            bgColor="tertiary"
            svgComponent={<QuarantineContactIcon />}
            withPadding="medium"
          />
        ),
        description: t('STATUS_DISCARDED_CARDS.quarantine'),
        title: 0,
        tooltip: t('STATUS_DISCARDED_CARDS.quarantineTooltip'),
        isLoading: true,
        filter: 'quarantine',
        onClick: () => {
          setStatusFilter(['quarantine']);
        },
        id: 'quarantine-card',
      },
      {
        icon: (
          <IconSvg
            fullWidth
            rounded
            bgColor="tertiary"
            svgComponent={<BlockedContactIcon />}
            withPadding="medium"
          />
        ),
        description: t('STATUS_DISCARDED_CARDS.blocked'),
        title: 0,
        tooltip: t('STATUS_DISCARDED_CARDS.blockedTooltip'),
        isLoading: true,
        filter: 'blocked',
        onClick: () => {
          setStatusFilter(['blocked']);
        },
        id: 'blocked-card',
      },
    ],
    [setStatusFilter, t]
  );

  return (
    <FilterCardsList
      cardsList={cardList}
      keyFilter="discarded"
      statusList={statusList}
      titleIsLoading={titleIsLoading}
    />
  );
};

export default memo(CardsFilterDiscarded);
